"use client";

import { useEffect, useState } from "react";
import BookCoverMini, { primaryGenre } from "@/components/book/BookCoverMini";
import { useUiTexts } from "@/lib/ui-texts-client";
import { isFinishedReread } from "@/lib/reading-position";

/** api/read-history の1件（このカードで使う項目だけ） */
export interface ReadHistoryEntry {
  novelId: string;
  readCount: number;
  readingProgress: number | null;
  lastOpenedAt: number | null;
  acquiredAt: number | null;
}

interface Props {
  histories: ReadHistoryEntry[];
  onOpen: (novelId: string) => void;
}

/**
 * ホームの右の列の真ん中: 読みかけの本（最後に開いて読み終えていない本）を出し、押すと続きから開く。
 * 読みかけが無ければ、いちばん新しく手に入れた未読の本。どちらも無ければ出さない
 */
export default function ContinueReadingCard({ histories, onOpen }: Props) {
  // 文は管理UIの「文言」で書き換えられる（src/lib/ui-texts.ts の home.continue.*）
  const { t } = useUiTexts();
  // 読了済みで最後まで来ている本は読みかけに数えない（前に残ってしまった 100% の位置も出さない）
  const reading = histories
    .filter((h) => h.readingProgress !== null && !isFinishedReread(h.readCount, h.readingProgress))
    .sort((a, b) => (b.lastOpenedAt ?? 0) - (a.lastOpenedAt ?? 0))[0];
  const unread = histories
    .filter((h) => h.readCount === 0)
    .sort((a, b) => (b.acquiredAt ?? 0) - (a.acquiredAt ?? 0))[0];
  const target = reading ?? unread;

  const [novel, setNovel] = useState<{ id: string; title: string; genre: string; format?: string } | null>(null);
  const [uiIcons, setUiIcons] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!target) return;
    fetch(`/api/novels/${target.novelId}`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setNovel)
      .catch(() => setNovel(null));
  }, [target?.novelId]); // eslint-disable-line react-hooks/exhaustive-deps

  // 表紙は書庫と同じ（管理画面で属性ごとの本のアイコンがあればそれを使う）
  useEffect(() => {
    fetch("/api/ui-icons")
      .then((r) => (r.ok ? r.json() : {}))
      .then(setUiIcons)
      .catch(() => {});
  }, []);

  if (!target || !novel || novel.id !== target.novelId) return null;
  const progress = reading ? Math.round((reading.readingProgress ?? 0) * 100) : null;

  return (
    <button
      type="button"
      onClick={() => onOpen(novel.id)}
      // ホームの選択肢と同じく、紐の付いたしおりの形（globals.css の .library-bookmark）
      className="library-continue library-bookmark"
      data-mark="koshi"
      aria-label={`${progress !== null ? t("home.continue.read") : t("home.continue.open")}：${novel.title}`}
    >
      <BookCoverMini
        genre={novel.genre}
        format={novel.format}
        iconUrl={uiIcons[`book_${primaryGenre(novel.genre)}`] ?? uiIcons["book_default"]}
      />
      <span className="min-w-0 flex-1">
        <span className="library-eyebrow">{progress !== null ? t("home.continue.eyebrowProgress") : t("home.continue.eyebrowUnread")}</span>
        <span className="library-continue-title">{novel.title}</span>
        {/* 進み具合は、紙の小口に挟んだしおりの位置で見せる（数字は読み上げにだけ渡す） */}
        {progress !== null && (
          <span role="img" aria-label={t("home.continue.progressAria", { 割合: progress })} className="relative mt-1.5 mb-2 block h-1.5">
            <span className="page-edge absolute inset-0 rounded-[1px]" />
            <span
              className="ribbon-tail absolute top-0 h-3.5 w-1.5 -translate-x-1/2 bg-lamp"
              style={{ left: `${Math.min(97, Math.max(3, progress))}%` }}
            />
          </span>
        )}
        <span className="library-continue-label"><span>{progress !== null ? t("home.continue.read") : t("home.continue.open")} <span aria-hidden="true">→</span></span>{progress !== null && <span>{t("home.continue.percent", { 割合: Math.min(100, Math.max(0, progress)) })}</span>}</span>
      </span>
    </button>
  );
}
